import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles, Lightbulb, ArrowRight, ShieldCheck, RefreshCw, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  getLeaderInsights,
  isLeaderInsightsEmpty,
  type LeaderInsights,
  type LeaderInsightsResponse,
} from '@/lib/admin/surveys-api';
import { downloadLeaderInsightsPdf } from '@/lib/admin/leaderInsightsPdf';

export function LeaderInsightsPanel({ surveyId }: { surveyId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const queryKey = ['leader-insights', surveyId];

  // Peek on mount — returns cached report if it exists, never generates.
  const peek = useQuery<LeaderInsightsResponse, Error>({
    queryKey,
    queryFn: () => getLeaderInsights(surveyId, { peek: true }),
    staleTime: 60_000,
  });

  const regenerate = useMutation<LeaderInsightsResponse, Error>({
    mutationFn: () => getLeaderInsights(surveyId, { force: true }),
    onSuccess: (fresh) => qc.setQueryData(queryKey, fresh),
    onError: (e) => toast({ variant: 'destructive', title: 'Insights failed', description: e.message }),
  });

  const raw = peek.data;
  const data: LeaderInsights | null = raw && !isLeaderInsightsEmpty(raw) ? raw : null;
  const stale = data?.stale ?? false;
  const isWorking = regenerate.isPending;
  const isLoadingPeek = peek.isLoading;

  // Auto-regenerate once per mount when the cached report is stale, so viewers
  // don't get served outdated AI prose just because nobody clicked the button.
  const autoRegenAttempted = useRef(false);
  useEffect(() => {
    if (
      !autoRegenAttempted.current &&
      data &&
      stale &&
      !isWorking &&
      !isLoadingPeek
    ) {
      autoRegenAttempted.current = true;
      regenerate.mutate();
    }
  }, [data, stale, isWorking, isLoadingPeek, regenerate]);

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          Confidential leader insights
          {data && (
            <Badge variant="outline" className="ml-1">
              <ShieldCheck className="h-3 w-3 mr-1" />
              k {data.k_floor_met ? '≥5 met' : '<5 — quant only'}
            </Badge>
          )}
          {stale && (
            <Badge variant="secondary" className="ml-1">
              New responses since last report
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          AI-summarized, anonymized themes and prioritized actions based on this survey's anchor scores
          and pooled conversation key-points. Nothing is attributed to an individual. The report is
          saved and stays put until you regenerate it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => regenerate.mutate()} disabled={isWorking || isLoadingPeek}>
              {isWorking ? (
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              ) : data ? (
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 mr-2" />
              )}
              {data ? (stale ? 'Regenerate with new responses' : 'Regenerate') : 'Generate insights'}
            </Button>
            {data && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadLeaderInsightsPdf(data, data.survey?.name ?? 'Leader insights')
                }
              >
                <FileDown className="h-3.5 w-3.5 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
          {data && (
            <span className="text-xs text-muted-foreground">
              {data.distinct_employees} distinct respondent
              {data.distinct_employees === 1 ? '' : 's'} · generated{' '}
              {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
        </div>

        {isLoadingPeek && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading saved report…
          </p>
        )}

        {!isLoadingPeek && !data && !isWorking && (
          <p className="text-sm text-muted-foreground">
            No report yet. Generation is on-demand — once generated, the report persists until you
            choose to regenerate.
          </p>
        )}

        {stale && data && (
          <p className="text-sm text-muted-foreground">
            New survey responses have arrived since this report was generated. Regenerate to see
            their impact on the systemic insight and suggested actions.
          </p>
        )}

        {data?.note && <p className="text-sm text-muted-foreground">{data.note}</p>}

        {data?.systemic_insight && (
          <div className="border border-border rounded-md p-4 bg-muted/30">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              Systemic insight
            </div>
            <p className="text-sm leading-relaxed">{data.systemic_insight}</p>
          </div>
        )}

        {data && data.priorities?.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
              Prioritize first
            </div>
            <ol className="space-y-1.5">
              {data.priorities.map((p, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="font-heading text-primary w-4">{i + 1}.</span>
                  <span>{p}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {data && data.per_anchor?.length > 0 && (
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Per-anchor breakdown
            </div>
            {data.per_anchor.map((a) => (
              <div key={a.anchor_id} className="border border-border rounded-md p-3 bg-card space-y-2">
                <div className="font-medium">{a.name}</div>
                {a.themes?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                      Themes
                    </div>
                    <ul className="list-disc list-inside text-sm space-y-0.5">
                      {a.themes.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {a.suggested_actions?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <Lightbulb className="h-3 w-3" /> Suggested actions
                    </div>
                    <ul className="space-y-1">
                      {a.suggested_actions.map((s, i) => (
                        <li key={i} className="flex gap-1.5 text-sm">
                          <ArrowRight className="h-3.5 w-3.5 mt-1 text-primary shrink-0" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {a.reflection_prompts?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                      Reflection prompts
                    </div>
                    <ul className="text-sm italic text-muted-foreground space-y-0.5">
                      {a.reflection_prompts.map((r, i) => (
                        <li key={i}>“{r}”</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
